import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

declare const Grapick: any;

export type GradientType = 'linear' | 'radial' | 'repeating-linear' | 'repeating-radial';
export type GradientDirection = 'top' | 'right' | 'center' | 'bottom' | 'left';

export function normalizeColorToHex(input: string): string {
  const raw = (input ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('#')) return raw.toUpperCase();

  const m = raw.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+)\s*)?\)$/i
  );
  if (!m) return raw;

  const toHex = (n: number) => {
    const v = Math.max(0, Math.min(255, Math.round(n)));
    return v.toString(16).padStart(2, '0').toUpperCase();
  };

  const r = Number(m[1]);
  const g = Number(m[2]);
  const b = Number(m[3]);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function buildGradientCss(
  type: GradientType,
  direction: GradientDirection,
  start: string,
  end: string
): string {
  const t = (type || 'linear').trim() as GradientType;
  const d = (direction || (t.includes('radial') ? 'center' : 'right')).trim() as GradientDirection;
  const startHex = normalizeColorToHex(start);
  const endHex = normalizeColorToHex(end);

  if (t.includes('radial')) {
    const at =
      d === 'top'
        ? 'circle at top'
        : d === 'right'
          ? 'circle at right'
          : d === 'bottom'
            ? 'circle at bottom'
            : d === 'left'
              ? 'circle at left'
              : 'circle at center';
    return `${t}-gradient(${at}, ${startHex} 0%, ${endHex} 100%)`;
  }

  const to =
    d === 'top'
      ? 'to top'
      : d === 'right'
        ? 'to right'
        : d === 'bottom'
          ? 'to bottom'
          : d === 'left'
            ? 'to left'
            : 'to right';
  return `${t}-gradient(${to}, ${startHex} 0%, ${endHex} 100%)`;
}

/** Pull first two color stops from a gradient() CSS string for reopening the picker. */
export function parseGradientStopsFromCss(css: string): { start: string; end: string } {
  const s = (css ?? '').trim();
  if (!s) return { start: '', end: '' };
  const re = /(#[0-9A-Fa-f]{3,8}|rgba?\([^)]+\))/gi;
  const colors = s.match(re) ?? [];
  return {
    start: colors[0] ? normalizeColorToHex(colors[0]) : '',
    end: colors[1] ? normalizeColorToHex(colors[1]) : colors[0] ? normalizeColorToHex(colors[0]) : '',
  };
}

export interface GradientApplyEvent {
  start: string;
  end: string;
  type: GradientType;
  direction: GradientDirection;
  cssValue: string;
}

@Component({
  selector: 'app-gradient-picker-dialog',
  templateUrl: './gradient-picker-dialog.component.html',
  styleUrls: ['./gradient-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradientPickerDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() header = '';
  @Input() dir: 'rtl' | 'ltr' | string = 'ltr';

  @Input() startColor = '#000000';
  @Input() endColor = '#FFFFFF';
  @Input() type: GradientType = 'linear';
  @Input() direction: GradientDirection = 'right';

  @Output() apply = new EventEmitter<GradientApplyEvent>();

  gradientCssValue = '';

  internalType: GradientType = 'linear';
  internalDirection: GradientDirection = 'right';

  readonly gradientTypeOptions: { value: GradientType; labelKey: string }[] = [
    { value: 'radial', labelKey: 'CARDADD.TEMPLATE.GRADIENT_TYPE_RADIAL' },
    { value: 'linear', labelKey: 'CARDADD.TEMPLATE.GRADIENT_TYPE_LINEAR' },
    { value: 'repeating-radial', labelKey: 'CARDADD.TEMPLATE.GRADIENT_TYPE_REPEATING_RADIAL' },
    { value: 'repeating-linear', labelKey: 'CARDADD.TEMPLATE.GRADIENT_TYPE_REPEATING_LINEAR' },
  ];

  readonly gradientDirectionOptions: { value: GradientDirection; labelKey: string }[] = [
    { value: 'top', labelKey: 'CARDADD.TEMPLATE.GRADIENT_DIR_TOP' },
    { value: 'right', labelKey: 'CARDADD.TEMPLATE.GRADIENT_DIR_RIGHT' },
    { value: 'center', labelKey: 'CARDADD.TEMPLATE.GRADIENT_DIR_CENTER' },
    { value: 'bottom', labelKey: 'CARDADD.TEMPLATE.GRADIENT_DIR_BOTTOM' },
    { value: 'left', labelKey: 'CARDADD.TEMPLATE.GRADIENT_DIR_LEFT' },
  ];

  private grapickInstance: any = null;
  private dialogId = `grapick-dialog-${Math.random().toString(36).slice(2)}`;

  constructor(private cd: ChangeDetectorRef) {}

  get grapickElId(): string {
    return this.dialogId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) this.internalType = this.type || 'linear';
    if (changes['direction']) this.internalDirection = this.direction || 'right';

    if (changes['startColor'] || changes['endColor'] || changes['type'] || changes['direction']) {
      const s = normalizeColorToHex(this.startColor);
      const e = normalizeColorToHex(this.endColor);
      this.gradientCssValue = buildGradientCss(this.internalType, this.internalDirection, s, e);
    }

    // When parent toggles visible off, ensure we cleanup.
    if (changes['visible'] && !this.visible) {
      this.destroyGrapick();
      this.gradientCssValue = '';
    }
  }

  onShow(): void {
    if (typeof Grapick === 'undefined') return;
    setTimeout(() => this.initGrapick(), 0);
  }

  onHide(): void {
    this.destroyGrapick();
    this.gradientCssValue = '';
    this.visibleChange.emit(false);
    this.cd.markForCheck();
  }

  onTypeChange(type: GradientType): void {
    this.internalType = type;
    this.grapickInstance?.setType?.(type);
    this.refreshCssValueFromCurrent();
  }

  onDirectionChange(direction: GradientDirection): void {
    this.internalDirection = direction;
    this.grapickInstance?.setDirection?.(direction);
    this.refreshCssValueFromCurrent();
  }

  applyAndClose(): void {
    this.refreshCssValueFromCurrent();
    let cssValue = '';
    try {
      cssValue = String(this.grapickInstance?.getValue?.() ?? '').trim();
    } catch (_) {
      cssValue = '';
    }
    if (!cssValue) {
      cssValue = this.gradientCssValue.trim();
    }
    const { start, end } = this.getTwoStopColors();
    if (!cssValue) {
      cssValue = buildGradientCss(this.internalType, this.internalDirection, start, end);
    }
    this.apply.emit({
      start,
      end,
      type: this.internalType,
      direction: this.internalDirection,
      cssValue,
    });
    this.visibleChange.emit(false);
    this.destroyGrapick();
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroyGrapick();
  }

  private initGrapick(): void {
    const el = document.querySelector(`#${this.dialogId}`);
    if (!el || this.grapickInstance) return;

    const start = normalizeColorToHex(this.startColor);
    const end = normalizeColorToHex(this.endColor);

    this.grapickInstance = new Grapick({
      el: `#${this.dialogId}`,
      direction: this.internalDirection,
      type: this.internalType,
      height: '40px',
      width: '100%',
    });

    this.grapickInstance.setValue(buildGradientCss(this.internalType, this.internalDirection, start, end), {
      silent: true,
    });

    this.enforceTwoGradientStops(start, end);

    this.grapickInstance.on('handler:add', (h: any) => {
      const handlers = this.grapickInstance?.getHandlers?.() ?? [];
      if (handlers.length > 2) {
        try {
          h?.remove?.({ silent: true });
          const two = this.getTwoStopColors();
          this.enforceTwoGradientStops(two.start, two.end);
        } catch (_) {}
      }
    });

    this.grapickInstance.on('change', () => {
      this.refreshCssValueFromCurrent();
    });

    this.refreshCssValueFromCurrent();
    this.cd.markForCheck();
  }

  private refreshCssValueFromCurrent(): void {
    const { start, end } = this.getTwoStopColors();
    this.gradientCssValue = buildGradientCss(this.internalType, this.internalDirection, start, end);
    this.cd.markForCheck();
  }

  private getTwoStopColors(): { start: string; end: string } {
    const gp = this.grapickInstance;
    if (!gp) {
      return {
        start: normalizeColorToHex(this.startColor),
        end: normalizeColorToHex(this.endColor),
      };
    }
    const handlers = (gp.getHandlers?.() ?? []).slice().sort((a: any, b: any) => a.position - b.position);
    if (handlers.length >= 2) {
      const first = handlers[0];
      const last = handlers[handlers.length - 1];
      return {
        start: normalizeColorToHex(first.getColor?.() ?? first.color ?? this.startColor),
        end: normalizeColorToHex(last.getColor?.() ?? last.color ?? this.endColor),
      };
    }
    return { start: normalizeColorToHex(this.startColor), end: normalizeColorToHex(this.endColor) };
  }

  private enforceTwoGradientStops(startColor: string, endColor: string): void {
    const gp = this.grapickInstance;
    if (!gp) return;
    const handlers = gp.getHandlers?.() ?? [];
    if (handlers.length <= 2) return;
    try {
      gp.clear({ silent: true });
      gp.addHandler(0, startColor, 1, { silent: true });
      gp.addHandler(100, endColor, 0, { silent: true });
      gp.change?.(1, { silent: true });
    } catch (_) {}
  }

  private destroyGrapick(): void {
    if (this.grapickInstance) {
      try {
        this.grapickInstance.destroy();
      } catch (_) {}
      this.grapickInstance = null;
    }
  }
}

